import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, Skeleton, Typography } from "@mui/material";
import { CustomInput } from "global/components";
import SettingsLayout from "../components/SettingsLayout";
import { useTitle } from "utils/UseTitle";
import strings from "global/constants/StringConstants";
import { hasAccessTo } from "layouts/Config";
import { useAppDispatch } from "redux/hooks";
import { setActiveWorkspace } from "redux/authSlice";
import { store } from "utils/store";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import { Workspace } from "types/workspace";
import {
  archiveWorkspace,
  fetchWorkspaces,
  renameWorkspace,
  unarchiveWorkspace,
} from "./WorkspacesServices";
import WorkspaceQuotaSection, { QuotaSkeleton } from "./WorkspaceQuotaSection";
import WorkspacesBackLink from "./components/WorkspacesBackLink";
import workspacesTokens from "./Workspaces.tokens";
import {
  buttonRowSx,
  dangerDescriptionSx,
  dangerTitleSx,
  formCardSx,
  formFieldsSx,
  nameCellSx,
  pillSx,
  sectionDescriptionSx,
  sectionDividerSx,
  sectionHeaderSx,
  sectionHeaderTextSx,
  sectionTitleSx,
} from "./Workspaces.styles";

const { pill } = workspacesTokens;

const WorkspaceDetails = () => {
  useTitle("Workspace details");
  const { id: workspaceId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const isAdmin = hasAccessTo(strings.resources.app, strings.adminPermission);

  const [ws, setWs] = useState<Workspace | null>(null);
  const [nameInput, setNameInput] = useState<string>("");
  // true from the start, so "Workspace not found." never flashes before the first load
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const load = async () => {
    if (!workspaceId) return;
    try {
      setLoading(true);
      const list = await fetchWorkspaces(true);
      const found =
        (Array.isArray(list) ? list : []).find((w) => w.id === workspaceId) ?? null;
      setWs(found);
      setNameInput(found?.name ?? "");
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Scope the active-workspace switch to this page's visit only — restore
    // whatever was active before on cleanup, so viewing a workspace's details
    // doesn't silently and permanently switch the user's active workspace
    // (which drives X-Workspace-Id and every hasWorkspacePermission check).
    const previousActiveWorkspaceId = store.getState().auth.activeWorkspaceId;
    if (workspaceId) {
      dispatch(setActiveWorkspace(workspaceId));
    }
    load();
    return () => {
      dispatch(setActiveWorkspace(previousActiveWorkspaceId));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const saveName = async () => {
    if (!ws || !nameInput.trim()) return;
    try {
      setSubmitting(true);
      await renameWorkspace(ws.id, nameInput.trim());
      openSuccessNotification("Workspace renamed.");
      await load();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleArchive = async () => {
    if (!ws) return;
    try {
      setSubmitting(true);
      if (ws.archived) {
        await unarchiveWorkspace(ws.id);
        openSuccessNotification("Workspace restored.");
      } else {
        await archiveWorkspace(ws.id);
        openSuccessNotification("Workspace archived.");
      }
      await load();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setSubmitting(false);
    }
  };

  const renderSkeleton = () => (
    <Box sx={formCardSx} aria-hidden="true">
      <Skeleton variant="text" width="30%" sx={{ fontSize: "15px" }} />
      <Box sx={formFieldsSx}>
        <Skeleton variant="rounded" height={44} />
      </Box>
      <Skeleton variant="rounded" width={120} height={40} />
      <QuotaSkeleton />
    </Box>
  );

  const renderCard = (workspace: Workspace) => (
    <Box sx={formCardSx}>
      <Box sx={sectionHeaderSx}>
        <Box sx={sectionHeaderTextSx}>
          <Box sx={nameCellSx}>
            <Typography component="h2" sx={sectionTitleSx}>
              Workspace name
            </Typography>
            <Box
              component="span"
              sx={pillSx(workspace.archived ? pill.warning : pill.brand)}
            >
              {workspace.archived ? "Archived" : "Active"}
            </Box>
            {workspace.isDefault && (
              <Box component="span" sx={pillSx(pill.neutral)}>
                Default
              </Box>
            )}
          </Box>
          <Typography component="div" sx={sectionDescriptionSx}>
            Shown in the workspace switcher and on this workspace's pages.
          </Typography>
        </Box>
      </Box>

      <Box sx={formFieldsSx}>
        <CustomInput
          id="workspaceName"
          label="Name"
          placeHolder="Workspace name"
          name="workspaceName"
          value={nameInput}
          onChange={(e: any) => setNameInput(e.target.value)}
          disabled={!isAdmin || workspace.archived}
        />
      </Box>

      {isAdmin && !workspace.archived && (
        <Box sx={buttonRowSx}>
          <Button
            variant="contained"
            onClick={saveName}
            disabled={
              submitting ||
              !nameInput.trim() ||
              nameInput.trim() === workspace.name
            }
          >
            Save name
          </Button>
        </Box>
      )}

      <WorkspaceQuotaSection
        workspaceId={workspace.id}
        isAdmin={isAdmin}
        archived={workspace.archived}
      />

      {isAdmin && (
        <Box sx={sectionDividerSx}>
          <Typography component="h3" sx={dangerTitleSx}>
            {workspace.archived ? "Restore workspace" : "Archive workspace"}
          </Typography>
          <Typography component="div" sx={dangerDescriptionSx}>
            {workspace.isDefault
              ? "The Default workspace can't be archived."
              : workspace.archived
                ? "Bring this workspace back to the active list."
                : "Hide this workspace from the list. You can restore it later."}
          </Typography>
          <Button
            variant="outlined"
            color={workspace.archived ? "primary" : "error"}
            onClick={toggleArchive}
            disabled={submitting || (!workspace.archived && workspace.isDefault)}
          >
            {workspace.archived ? "Restore" : "Archive"}
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <SettingsLayout
      activeTab="Workspaces"
      title={ws ? `${ws.name} — Details` : "Workspace details"}
    >
      <WorkspacesBackLink />

      {/* Skeleton on the first load only: a reload after rename or archive keeps the page in place. */}
      {loading && !ws ? (
        renderSkeleton()
      ) : !ws ? (
        <Box sx={formCardSx}>
          <Typography sx={sectionDescriptionSx}>
            This workspace couldn't be found. It may have been removed.
          </Typography>
        </Box>
      ) : (
        renderCard(ws)
      )}
    </SettingsLayout>
  );
};

export default WorkspaceDetails;
